import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { LedgerField, LedgerInput } from '../ui';

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

type ForgotValues = yup.InferType<typeof validationSchema>;

export const ForgotPasswordForm: React.FC = () => {
  const { requestPasswordReset, isLoading, error } = useAuth();
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotValues>({ resolver: yupResolver(validationSchema), mode: 'onTouched' });

  const onSubmit = async (values: ForgotValues) => {
    try {
      await requestPasswordReset(values);
      setSubmittedEmail(values.email);
    } catch {
      // The auth slice owns the error state.
    }
  };

  if (submittedEmail) {
    return (
      <div>
        <h2 className="mb-2 font-serif text-scale-3 text-ink">Check your email</h2>
        <p className="text-scale-1 text-ink-muted">
          If an account exists with the email {submittedEmail}, we've sent instructions to reset your password.
        </p>
        <p className="mt-4 text-center text-scale-1">
          <RouterLink to="/login" className="text-accent hover:underline">
            ← Back to sign in
          </RouterLink>
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-2 font-serif text-scale-3 text-ink">Forgot password</h2>
      <p className="mb-3 text-scale-1 text-ink-muted">
        Enter your email address and we'll send you instructions to reset your password.
      </p>
      {error && (
        <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
          {error}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LedgerField label="Email address" htmlFor="email" error={errors.email?.message}>
          <LedgerInput id="email" type="email" autoComplete="email" autoFocus invalid={!!errors.email} {...register('email')} />
        </LedgerField>
        <button
          type="submit"
          disabled={isLoading}
          className="mt-1 w-full cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:opacity-60"
        >
          {isLoading ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      <p className="mt-4 text-center text-scale-1">
        <RouterLink to="/login" className="text-accent hover:underline">
          ← Back to sign in
        </RouterLink>
      </p>
    </div>
  );
};
